const CREDITS_PER_DOLLAR = 100;

function formatNumber(n: number, minDecimals: number, maxDecimals: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Formats a credit amount with adaptive precision.
 *   0       → "0"
 *   1234    → "1,234"
 *   12.5    → "12.50"
 *   0.03    → "0.03"
 *   0.0012  → "0.0012"
 */
export function formatCredits(credits: number): string {
  const abs = Math.abs(credits);
  if (abs === 0) return '0';
  if (Number.isInteger(credits)) return credits.toLocaleString();
  if (abs >= 0.01) return formatNumber(credits, 2, 2);
  return formatNumber(credits, 2, 4);
}

/**
 * Converts credits to a dollar string: "$1.23", "$0.0003".
 * Uses enough decimal places to show at least one significant digit.
 */
export function formatDollars(credits: number): string {
  const dollars = credits / CREDITS_PER_DOLLAR;
  const abs = Math.abs(dollars);
  if (abs === 0) return '$0.00';
  if (abs >= 0.01) return `$${formatNumber(dollars, 2, 2)}`;
  if (abs >= 0.001) return `$${formatNumber(dollars, 4, 4)}`;
  return `$${formatNumber(dollars, 4, 6)}`;
}

/**
 * Formats credits with the dollar equivalent in parentheses.
 *   -0.03   → "-0.03 ($0.0003)"
 *   150     → "150 ($1.50)"
 *   0       → "0 ($0.00)"
 */
export function formatCreditsWithDollar(credits: number): string {
  return `${formatCredits(credits)} (${formatDollars(credits)})`;
}

/**
 * Formats a signed transaction amount with dollar equivalent.
 *   -0.03   → "-0.03 (-$0.0003)"
 *   200     → "+200 (+$2.00)"
 *   0       → "0 ($0.00)"
 */
export function formatTransactionAmount(credits: number): string {
  const prefix = credits > 0 ? '+' : '';
  return `${prefix}${formatCredits(credits)} (${prefix}${formatDollars(credits)})`;
}
